/* eslint-disable no-param-reassign */
/* eslint-disable react/forbid-prop-types */
import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { ResponsiveBar } from '@nivo/bar';

import CustomLegend from '../../utils/chartTools/createLegend';
import customTooltip from '../../utils/chartTools/handleCustomTooltip';
import colorSelector from '../../utils/formatCharts/handleColor';
import {
  customPercentAxisRight,
  customPercentAxisLeft,
  customPercentAxisTop,
  customPercentAxisBottom,
  customIntervalAxisRight,
  customIntervalAxisLeft,
  customIntervalAxisTop,
  customIntervalAxisBottom,
} from '../../utils/formatCharts/formatAxis';
import { staticPercent } from '../../utils/formatCharts/handleCustomValue';
import { genDataHistogram } from '../../utils/formatCharts/handleData';
import KpiStatus from '../../utils/chartTools/KpiStatus';
import CustomLabel from './CustomLabel';

const emptyObj = {};

const Histogram = ({
  data, styleConfig, globalConfig = emptyObj,
}) => {
  const [chartData, setChartData] = useState({ ready: false });

  useEffect(() => {
    setChartData(genDataHistogram(data, styleConfig));
  }, [
    data,
    styleConfig.DataFormatValueType,
    styleConfig.DataFormatValueTypeAlt,
  ]);

  const createTooltip = (val) => customTooltip(val, 'histogram', styleConfig);

  const createLabel = (label, x) => {
    const {
      LayoutControl,
      LabelFormatControl,
      Decimals,
      separadorNumericoCasas,
      DataFormatValueType,
      DataFormatValueTypeAlt,
    } = styleConfig;
    let offset = -10;
    let val = DataFormatValueType === 'percentual' ? (
      staticPercent(label.data[label.id] ?? 0, Decimals, separadorNumericoCasas)
    ) : label.data[label.id] ?? 0;
    if (styleConfig?.DataFormatValueTypeAlt !== 'none') {
      const val2 = DataFormatValueTypeAlt === 'percentual' ? (
        staticPercent(label.data[`${label.id}-value2`] ?? 0, Decimals, separadorNumericoCasas)
      ) : label.data[`${label.id}-value2`] ?? 0;
      if (styleConfig.LabelLineBreak && styleConfig?.DataFormatAlt !== 'none') {
        val = (
          <>
            <tspan>{val}</tspan>
            <tspan x={x} dy="1.2em">{`(${val2})`}</tspan>
          </>
        );
        offset = -25;
      } else val = `${val} (${val2})`.trim();
    }
    if (LayoutControl !== 'vertical' || LabelFormatControl === 'Central') return val;
    return <tspan y={offset}>{val}</tspan>;
  };

  return chartData.ready ? (
    <KpiStatus
      conditions={([
        [
          data?.index?.[0] === 'Totais', // TODO
        ],
      ])}
    >
      <ResponsiveBar
        data={chartData.data}
        keys={chartData.keys}
        indexBy="label"
        /* GENERAL */
        margin={styleConfig.Margin}
        layout={styleConfig.LayoutControl}
        padding={styleConfig?.PaddingControl?.value ?? 0}
        /* BORDER */
        borderWidth={styleConfig.BorderWidthControl.value}
        borderColor={styleConfig.BorderColorControl}
        /* LABEL */
        enableLabel={false}
        labelSkipWidth={styleConfig.labelSkipWidth || 0}
        labelSkipHeight={styleConfig.labelSkipHeight || 0}
        labelTextColor={styleConfig.LabelTextColorControl}
        // label={createLabel}
        /* AXIS */
        enableGridX={styleConfig.EnableGridX}
        enableGridY={styleConfig.EnableGridY}
        axisTop={styleConfig.LayoutControl === 'horizontal' ? (
          customPercentAxisTop(styleConfig, globalConfig, null, false)
        ) : (
          customIntervalAxisTop(styleConfig)
        )}
        axisLeft={styleConfig.LayoutControl !== 'horizontal' ? (
          customPercentAxisLeft(styleConfig, globalConfig, null, false)
        ) : (
          customIntervalAxisLeft(styleConfig)
        )}
        axisBottom={styleConfig.LayoutControl === 'horizontal' ? (
          customPercentAxisBottom(styleConfig, globalConfig, null, false)
        ) : (
          customIntervalAxisBottom(styleConfig)
        )}
        axisRight={styleConfig.LayoutControl !== 'horizontal' ? (
          customPercentAxisRight(styleConfig, globalConfig, null, false)
        ) : (
          customIntervalAxisRight(styleConfig)
        )}
        /* LEGEND */
        layers={[
          'grid',
          'axes',
          'bars',
          (dt) => (styleConfig.EnableLabelControl?.checked === false ? null : CustomLabel({
            data: dt.bars, styleConfig, globalConfig, content: createLabel,
          })),
          'markers',
          styleConfig?.LegendControl?.checked ? (dt) => (
            <CustomLegend
              chartDataAr={chartData.keys}
              colorParts={dt.bars.reduce((aux, p) => {
                aux[p.data.id] = p.color;
                return aux;
              }, {})}
              sizes={{
                width: dt.innerWidth,
                height: dt.innerHeight,
              }}
              config={styleConfig}
              isEdit
            />
          ) : null,
          'annotations',
        ]}
        /* TOOLTIP */
        isInteractive={styleConfig.InteractiveControl}
        tooltip={createTooltip}
        /* STYLE */
        colors={colorSelector({
          dataLength: chartData.data.length,
          config: styleConfig,
          colorKey: 'id',
          globalConfig,
        })}
        /* THEME */
        theme={{
          legends: {
            text: {
              fontSize: `${styleConfig?.LegendsFontSizeControl?.value}px`,
              fontFamily: `'${globalConfig?.useGlobalFont ? globalConfig.fontLegend : styleConfig?.LegendsFontFamilyControl?.value}', Arial`,
              fill: styleConfig?.LegendFontColor ?? '#999',
              fontWeight: styleConfig?.LegendBold ? 'bold' : 'normal',
              fontStyle: styleConfig?.LegendItalic ? 'italic' : 'normal',
            },
            hidden: {
              text: {
                textDecoration: 'line-through',
              },
            },
          },
          labels: {
            text: {
              fontSize: `${styleConfig?.LabelsFontSizeControl ? styleConfig?.LabelsFontSizeControl.value : 13}px`,
              fontFamily: `'${globalConfig?.useGlobalFont ? globalConfig.fontLabel : styleConfig?.LabelsFontFamilyControl?.value}', Arial`,
              transform: `translate(${styleConfig?.LabelTranslateX ? styleConfig?.LabelTranslateX : 0}px, ${styleConfig?.LabelTranslateY ? styleConfig?.LabelTranslateY : 0}px) rotate(${styleConfig?.LabelRotationControl ? styleConfig?.LabelRotationControl : 0}deg)`,
              transformOrigin: 'center center',
              transformBox: 'fill-box',
            },
          },
          axis: {
            legend: {
              text: {
                fontSize: styleConfig.AxisGlobal.legendFontSize,
                fontFamily: `'${globalConfig?.useGlobalFont ? globalConfig.fontAxis : styleConfig.AxisGlobal.fontFamily}', Arial`,
                fill: styleConfig.AxisGlobal.color,
              },
            },
            ticks: {
              text: {
                fontFamily: `'${globalConfig?.useGlobalFont ? globalConfig.fontAxis : styleConfig.AxisGlobal.fontFamily}', Arial`,
                fontSize: styleConfig.AxisGlobal.fontSize,
                fill: styleConfig.AxisGlobal.color,
              },
            },
          },
        }}
        /* ETC */
        animate={styleConfig.InteractiveControl ? !!styleConfig.Animate : false}
        motionConfig={styleConfig.InteractiveControl ? (styleConfig.MotionConfig || 'default') : 'default'}
      />
    </KpiStatus>
  ) : <></>;
};

Histogram.propTypes = {
  data: PropTypes.objectOf(PropTypes.any).isRequired,
  styleConfig: PropTypes.objectOf(PropTypes.any).isRequired,
  globalConfig: PropTypes.objectOf(PropTypes.any),
};

export default Histogram;
